import { FileText } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { DdqSession } from '@/types/api.types';
import { SessionStatusBadge } from './SessionStatusBadge';
import { SessionActions, type ExportFormat } from './SessionActions';
import { formatSessionDate } from '../utils/formatSessionDate';

export interface SessionCardProps {
  session: DdqSession;
  onSelect: () => void;
  onReview: () => void;
  onExport: (format: ExportFormat) => void;
  onDelete: () => void;
  className?: string;
}

function getActionLabel(status: DdqSession['status']): string | null {
  switch (status) {
    case 'Ready':
      return 'Review';
    case 'Processing':
    case 'Pending':
      return 'View Progress';
    case 'Error':
      return 'View Details';
    default:
      return null;
  }
}

export function SessionCard({
  session,
  onSelect,
  onReview,
  onExport,
  onDelete,
  className,
}: SessionCardProps) {
  const actionLabel = getActionLabel(session.status);

  return (
    <Card
      className={cn(
        'hover:shadow-md transition-shadow cursor-pointer',
        className
      )}
      onClick={onSelect}
    >
      <CardContent className="py-4 px-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <FileText className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-base truncate" title={session.fileName}>
                  {session.fileName}
                </h3>
                <SessionStatusBadge status={session.status} />
              </div>
              <p className="text-sm text-muted-foreground">
                {session.totalQuestions} questions
                <span className="mx-1.5">&middot;</span>
                {session.automationRate.toFixed(0)}% auto-filled
                <span className="mx-1.5">&middot;</span>
                {formatSessionDate(session.created)}
              </p>
            </div>
          </div>

          <div
            className="flex items-center gap-2 shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            {actionLabel && (
              <Button
                variant={session.status === 'Ready' ? 'default' : 'outline'}
                size="sm"
                onClick={onReview}
              >
                {actionLabel}
              </Button>
            )}
            <SessionActions
              session={session}
              onReview={onReview}
              onExport={onExport}
              onDelete={onDelete}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
